import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { platformApi, PLATFORM_TOKEN_KEY } from '../api/apiClient';
import { useCoreT } from '../context/CoreLangContext';
import PhoneOtpForm, { buildOtpLabels } from '../components/PhoneOtpForm';

const PLATFORM_PROFILE_KEY = 'nooshin_platform_profile';

export default function PlatformAdminLoginPage({ onLogin }) {
  const navigate = useNavigate();
  const T = useCoreT();
  const [method, setMethod] = useState('phone'); // 'phone' | 'email' (email = username/email + password)
  const [form,  setForm]  = useState({ identifier: '', password: '' });
  const [error, setError] = useState('');
  const [busy,  setBusy]  = useState(false);

  const set = k => e => setForm(f => ({ ...f, [k]: e.target.value }));

  const finishLogin = (res) => {
    localStorage.setItem(PLATFORM_TOKEN_KEY,   res.token);
    localStorage.setItem(PLATFORM_PROFILE_KEY, JSON.stringify(res.data));
    onLogin(res.data);
    navigate('/platform-admin');
  };

  const handleLogin = async () => {
    setError('');
    if (!form.identifier || !form.password) { setError(T.error); return; }
    setBusy(true);
    try {
      const res = await platformApi.login(form);
      finishLogin(res);
    } catch (e) { setError(e.message); }
    finally { setBusy(false); }
  };

  return (
    <div className="pal-root" dir={T.dir}>
      <div className="pal-card">
        <div className="pal-logo">🏛️</div>
        <h1 className="pal-title">{T.platformName}</h1>
        <p className="pal-sub">{T.platformLoginSub}</p>

        {error && <div className="pal-error">{error}</div>}

        <div className="pal-method">
          <button className={`pal-method-btn ${method==='phone'?'active':''}`} onClick={() => { setMethod('phone'); setError(''); }}>📱 Phone</button>
          <button className={`pal-method-btn ${method==='email'?'active':''}`} onClick={() => { setMethod('email'); setError(''); }}>🔑 {T.identifier || 'Email'}</button>
        </div>

        {method === 'phone' ? (
          <div style={{ width: '100%' }}>
            <PhoneOtpForm
              accent="#6366f1"
              lang={T.lang}
              labels={buildOtpLabels(T)}
              onSend={(phone) => platformApi.otpSend(phone).then(r => ({ success: true, ...r })).catch(e => ({ success: false, error: e.message }))}
              onVerify={(phone, code) => platformApi.otpVerify(phone, code).then(r => { finishLogin(r); return { success: true }; }).catch(e => ({ success: false, error: e.message }))}
            />
          </div>
        ) : (
          <>
            <div className="pal-fields">
              <div className="pal-field">
                <label>{T.identifier || T.email}</label>
                <input className="pal-input" type="text" value={form.identifier} onChange={set('identifier')}
                  placeholder="root  ·  root@nowshin.ir" onKeyDown={e => e.key==='Enter' && handleLogin()} autoFocus />
              </div>
              <div className="pal-field">
                <label>{T.password}</label>
                <input className="pal-input" type="password" value={form.password} onChange={set('password')}
                  placeholder="••••••••" onKeyDown={e => e.key==='Enter' && handleLogin()} />
              </div>
            </div>

            <button className="pal-btn" onClick={handleLogin} disabled={busy}>
              {busy ? T.signingIn : T.platformSignIn}
            </button>
          </>
        )}
      </div>

      <style>{`
        *{box-sizing:border-box;margin:0;padding:0}
        .pal-root{min-height:100vh;background:#080810;display:flex;align-items:center;justify-content:center;padding:2rem;font-family:'DM Sans',sans-serif}
        .pal-card{background:rgba(255,255,255,.04);border:1px solid rgba(255,255,255,.1);border-radius:24px;padding:2.5rem;width:100%;max-width:380px;display:flex;flex-direction:column;align-items:center;gap:1rem}
        .pal-logo{font-size:3rem}
        .pal-title{font-family:'Playfair Display',serif;font-size:1.5rem;color:#fff;text-align:center}
        .pal-sub{font-size:.78rem;color:rgba(255,255,255,.3);text-align:center;letter-spacing:.04em}
        .pal-error{background:rgba(239,68,68,.1);border:1px solid rgba(239,68,68,.3);border-radius:10px;padding:.7rem 1rem;color:#fca5a5;font-size:.82rem;width:100%;text-align:center}
        .pal-method{display:flex;gap:.5rem;width:100%}
        .pal-method-btn{flex:1;padding:.6rem;background:rgba(255,255,255,.04);border:1px solid rgba(255,255,255,.1);border-radius:10px;color:rgba(255,255,255,.55);cursor:pointer;font-family:'DM Sans',sans-serif;font-size:.85rem;font-weight:600;transition:all .2s}
        .pal-method-btn.active{background:linear-gradient(135deg,#6366f1,#8b5cf6);border-color:#6366f1;color:#fff}
        .pal-fields{width:100%;display:flex;flex-direction:column;gap:.875rem}
        .pal-field{display:flex;flex-direction:column;gap:.35rem}
        .pal-field label{font-size:.7rem;color:rgba(255,255,255,.4);text-transform:uppercase;letter-spacing:.08em}
        .pal-input{background:rgba(255,255,255,.06);border:1px solid rgba(255,255,255,.1);border-radius:10px;padding:.8rem 1rem;color:#fff;font-family:'DM Sans',sans-serif;font-size:.9rem;outline:none;transition:border-color .2s;width:100%}
        .pal-input:focus{border-color:#6366f1}
        .pal-btn{width:100%;padding:.9rem;background:linear-gradient(135deg,#6366f1,#8b5cf6);border:none;border-radius:12px;color:#fff;font-family:'DM Sans',sans-serif;font-size:.95rem;font-weight:700;cursor:pointer;transition:opacity .2s;margin-top:.25rem}
        .pal-btn:hover:not(:disabled){opacity:.88}
        .pal-btn:disabled{opacity:.5;cursor:not-allowed}
        .pal-hint{font-size:.72rem;color:rgba(255,255,255,.2);text-align:center;font-family:monospace}
      `}</style>
    </div>
  );
}
